import Mux from '@mux/mux-player-react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { isEmpty } from 'validate.js';

import ClickableDiv from 'dpl/common/components/ClickableDiv';
import MediaCarouselSplide, {
  DEFAULT_SPLIDE_OPTIONS
} from 'dpl/common/components/MediaCarouselSplide';
import { COLOR_OPTIONS } from 'dpl/common/design_system/utils/constants';
import SmartImage from 'dpl/components/SmartImage';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import withLazyLoading from 'dpl/decorators/withLazyLoading';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import {
  BADGE_POSITIONS,
  CAROUSEL_TYPES
} from 'dpl/puppy_search_results/utils/constants';
import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import { sendAnalyticsEvent, sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import CardBadge from 'dpl/experiments/exp_breeder_search_updates/breeder_search_results/components/CardBadge';
import VideoIconOverlay from 'dpl/breeder_search_results/components/VideoIconOverlay';

const COMMON_ARROW_CLASS_NAME = 'bg-white black f6';

const WRAPPER_CLASSES = 'CardMediaCarouselFile h-100 db relative';

const IS_LT_LG = isLessThanBreakpoint('lg');

export function CardMediaCarouselFile({
  badgeProps,
  mediaFile,
  smartImageProps,
  analytics,
  carouselPosition,
  muxRef
}) {
  const [isPlaying, setIsPlaying] = useState(false);

  const {
    alt,
    analytics: imageAnalytics,
    experiment_clickthrough_url: clickthroughUrl,
    url
  } = mediaFile;

  const puppyId = mediaFile.puppy_id || null;
  const isVideo = mediaFile.type === 'Video';

  function handleImageClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (isVideo) {
      return;
    }

    if (imageAnalytics) {
      sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, imageAnalytics);
    }

    if (puppyId) {
      sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
        ...analytics,
        view_feature: ANALYTICS_VIEW_FEATURES.PUPPY_PHOTO,
        dog_id: puppyId,
        carousel_position: carouselPosition
      });

      openPuppyPage({ puppyId });
    } else {
      sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, analytics);

      window.location.href = clickthroughUrl;
    }
  }

  const carouselImage = !isVideo && (
    <>
      <SmartImage {...smartImageProps} alt={alt} src={url} />
      {!isEmpty(badgeProps) && (
        <div
          className={classnames(
            'absolute absolute--fill pointer-events-none pv3 ph3',
            {
              'self-end': badgeProps.position === BADGE_POSITIONS.BOTTOM
            }
          )}
        >
          <CardBadge color={badgeProps.color}>{badgeProps.text}</CardBadge>
        </div>
      )}
    </>
  );

  const carouselMediaFile = isVideo ? (
    <div className="CarouselMediaFile relative">
      <Mux
        ref={muxRef}
        playbackId={mediaFile.playback_ids?.[0]?.id}
        style={{
          '--bottom-controls': 'none',
          '--center-seek-forward-button': 'none',
          '--center-seek-backward-button': 'none',
          '--bottom-play-button': 'none',
          '--bottom-volume-range': 'none',
          '--bottom-time-range:': 'none',
          '--bottom-time-display': 'none',
          '--bottom-duration-display': 'none',
          '--bottom-mute-button': 'none',
          '--bottom-captions-button': 'none',
          '--bottom-airplay-button': 'none',
          '--bottom-pip-button': 'none',
          '--bottom-playback-rate-button': 'none',
          width: smartImageProps.width,
          height: smartImageProps.height
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        preload="none"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      <VideoIconOverlay
        className="absolute absolute--fill pointer-events-none z-1"
        isPlaying={isPlaying}
      />
    </div>
  ) : (
    carouselImage
  );

  if (puppyId || clickthroughUrl || isVideo) {
    const fileType = isVideo ? 'video' : 'image';
    // HACK: using ClickableDiv since the outer Card is already an anchor
    return (
      <ClickableDiv
        data-test-id={`card-media-carousel-${fileType}-${mediaFile.id}`}
        className={WRAPPER_CLASSES}
        style={{
          width: smartImageProps.width
        }}
        onClick={handleImageClick}
      >
        {carouselMediaFile}
      </ClickableDiv>
    );
  }

  return (
    <div
      className={WRAPPER_CLASSES}
      data-test-id={`card-media-carousel-image-${mediaFile.id}`}
    >
      {carouselImage}
    </div>
  );
}

CardMediaCarouselFile.propTypes = {
  mediaFile: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    analytics: PropTypes.shape({
      view: PropTypes.string.isRequired,
      view_feature: PropTypes.string.isRequired,
      view_feature_location: PropTypes.string.isRequired
    }),
    url: PropTypes.string,
    alt: PropTypes.string,
    experiment_clickthrough_url: PropTypes.string,
    playback_ids: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        policy: PropTypes.string.isRequired
      })
    ),
    status: PropTypes.string,
    aspect_ratio: PropTypes.string,
    type: PropTypes.string,
    puppy_id: PropTypes.number
  }).isRequired,
  analytics: PropTypes.shape({
    availability: PropTypes.string,
    breed_id: PropTypes.number,
    breeder_profile_id: PropTypes.number,
    position: PropTypes.number,
    view: PropTypes.string.isRequired,
    view_feature: PropTypes.string,
    view_feature_location: PropTypes.string.isRequired
  }),
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  muxRef: PropTypes.object,
  badgeProps: PropTypes.shape({
    text: PropTypes.string.isRequired,
    color: PropTypes.oneOf(COLOR_OPTIONS).isRequired,
    position: PropTypes.string
  }),
  carouselPosition: PropTypes.number
};

CardMediaCarouselFile.defaultProps = {
  badgeProps: null,
  analytics: {},
  muxRef: null,
  carouselPosition: null
};

const CardMediaCarousel = React.memo(function CardMediaCarousel({
  mediaFiles,
  smartImageProps,
  isInViewport,
  setLazyRef,
  showCaptions,
  isPuppyCard,
  analytics
}) {
  const splideRef = useRef(null);

  const splideOptions = {
    ...DEFAULT_SPLIDE_OPTIONS,
    gap: IS_LT_LG ? 4 : 8,
    type: CAROUSEL_TYPES.SLIDE,
    fixedWidth: 260,
    autoWidth: true,
    padding: { left: 24, right: 24 }
  };

  return (
    <div className="CardMediaCarousel">
      <div
        style={{
          minHeight: smartImageProps.height || '1px',
          minWidth: smartImageProps.width || '1px'
        }}
        ref={setLazyRef}
      >
        {isInViewport && (
          <MediaCarouselSplide
            splideClassName="BreederSearchResultsPhoto h-100 f0"
            leftArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, {
              ml8: !isPuppyCard,
              ml4: isPuppyCard
            })}
            rightArrowClassName={classnames(COMMON_ARROW_CLASS_NAME, 'mr4')}
            mediaFiles={mediaFiles}
            ref={splideRef}
            CarouselMediaComponent={CardMediaCarouselFile}
            smartImageProps={smartImageProps}
            splideOptions={splideOptions}
            showCaptions={showCaptions}
            hasPhotoBullets={isPuppyCard}
            bulletStyle={{ transform: 'scale(0.85)', margin: '2px' }}
            analytics={analytics}
          />
        )}
      </div>
    </div>
  );
});

CardMediaCarousel.propTypes = {
  mediaFiles: PropTypes.arrayOf(
    PropTypes.shape({
      ...PhotographPropTypes,
      url: PropTypes.string,
      urls: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  // eslint-disable-next-line react/forbid-prop-types
  smartImageProps: PropTypes.object.isRequired,
  showCaptions: PropTypes.bool,
  isInViewport: PropTypes.bool.isRequired,
  setLazyRef: PropTypes.func,
  isPuppyCard: PropTypes.bool,
  analytics: PropTypes.shape({
    position: PropTypes.number,
    view: PropTypes.string
  })
};

CardMediaCarousel.defaultProps = {
  setLazyRef: null,
  mediaFiles: [],
  showCaptions: false,
  isPuppyCard: false,
  analytics: {}
};

export default withLazyLoading({
  defaultLazy: true
})(CardMediaCarousel);
